import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api/client';

/* ───────────────────────────────────────────────────────────────────────────
   Contrato — CRUD /fiscal/formulas (FiscalConfigController)

   Espelha `TaxFormulaResponseDto` e os DTOs de create/update. A criação e a
   atualização re-validam a expressão no servidor (FormulaEvaluatorService) —
   o `validation` na resposta indica se a sintaxe é válida e quais variáveis
   a expressão referencia.

   Envelope paginado {data, total, page, limit, totalPages}.
   O tenant vem da sessão.
   ─────────────────────────────────────────────────────────────────────────── */

export interface TaxFormula {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  expression: string;
  variables: string[];
  validation: { valid: boolean; errors: string[]; variables: string[] } | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TaxFormulaInput {
  name: string;
  description?: string;
  expression: string;
  enabled?: boolean;
}

export interface PaginatedFormulas {
  data: TaxFormula[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const FORMULAS_KEY = ['fiscal', 'formulas'] as const;

export function useTaxFormulas(params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: [FORMULAS_KEY, params] as const,
    queryFn: () =>
      apiClient.get<PaginatedFormulas>('/fiscal/formulas', {
        params: {
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 50),
        },
      }),
  });
}

export function useCreateTaxFormula() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TaxFormulaInput) =>
      apiClient.post<TaxFormula>('/fiscal/formulas', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FORMULAS_KEY });
    },
  });
}

export function useUpdateTaxFormula() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: TaxFormulaInput & { id: string }) =>
      apiClient.put<TaxFormula>(`/fiscal/formulas/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FORMULAS_KEY });
    },
  });
}

export function useDeleteTaxFormula() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/fiscal/formulas/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FORMULAS_KEY });
    },
  });
}